import styled, { createGlobalStyle } from 'styled-components';

// Styles
import { hoverCapable } from '../../../../styles/hover';

// Estilos globales para el DayPicker convertidos de CSS a styled-components
export const DayPickerGlobalStyles = createGlobalStyle`
  .rdp {
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.black};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    font-size: ${({ theme }) => theme.fontSize.S};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    z-index: 999;

    --rdp-cell-size: 35px;
    --rdp-greydark-color: ${({ theme }) => theme.colors.greyDark};
    --rdp-greyclear-color: ${({ theme }) => theme.colors.greyClear};
    --rdp-greynormal-color: ${({ theme }) => theme.colors.greyLight};
    --rdp-range-color: ${({ theme }) => theme.colors.paleGrey};
    --rdp-rangestart-color: ${({ theme }) => theme.colors.greySoftLight};
    --rdp-background-color: ${({ theme }) => theme.colors.veryClearRed};
    --rdp-outline: 1px solid ${({ theme }) => theme.colors.garnet};
    --rdp-outline-selected: 3px solid ${({ theme }) => theme.colors.garnet};
  }

  /* Hide elements for devices that are not screen readers */
  .rdp-vhidden {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    background: transparent;
    border: 0;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    position: absolute !important;
    top: 0;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    overflow: hidden !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    border: 0 !important;
  }

  /* Buttons */
  .rdp-button_reset {
    appearance: none;
    position: relative;
    margin: 0;
    padding: 0;
    cursor: default;
    color: inherit;
    background: none;
    font: inherit;
    -moz-appearance: none;
    -webkit-appearance: none;
  }

  .rdp-button_reset:focus-visible {
    outline: none;
  }

  .rdp-button {
    border: none;
  }

  .rdp-button[disabled]:not(.rdp-day_selected) {
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    color: ${({ theme }) => theme.colors.greyLight};
    cursor: auto;
  }

  .rdp-button:not([disabled]) {
    cursor: pointer;
  }

  /* Solo con puntero fino: en táctil el día tocado se quedaba con el fondo gris
     pegado, indistinguible del día seleccionado (ver styles/hover.ts). */
  ${hoverCapable} {
    .rdp-button:hover:not([disabled]):not(.rdp-day_selected):not(.rdp-nav_button) {
      background-color: var(--rdp-greyclear-color);
    }
  }

  .rdp-nav_button:nth-of-type(1):disabled {
    color: var(--rdp-greynormal-color);
  }

  .rdp-nav_button:nth-of-type(1) {
    color: var(--rdp-greydark-color);
  }

  .rdp-button:focus-visible:not([disabled]) {
    color: inherit;
    background-color: var(--rdp-range-color);
    border: 1.5px solid var(--rdp-greydark-color);
  }

  .rdp-months {
    display: flex;
    gap: ${({ theme }) => theme.spacing.xl};
  }

  .rdp-month {
    width: 100%;
    margin: 0;
  }

  .rdp-month:first-child {
    margin-left: 0;
  }

  .rdp-month:last-child {
    margin-right: 0;
  }

  .rdp-table {
    width: 100%;
    border-collapse: collapse;
  }

  .rdp-with_weeknumber .rdp-table {
    max-width: calc(var(--rdp-cell-size) * 8);
    border-collapse: collapse;
  }

  .rdp-caption {
    display: flex;
    justify-content: space-between;
    height: var(--rdp-cell-size);
  }

  .rdp-multiple_months .rdp-caption {
    position: relative;
    display: block;
    text-align: center;
  }

  .rdp-caption_dropdowns {
    position: relative;
    display: inline-flex;
  }

  .rdp-caption_label {
    position: relative;
    margin: 0;
    white-space: nowrap;
  }

  .rdp-multiple_months .rdp-caption_start .rdp-nav {
    position: absolute;
    top: 0;
    left: 0;
  }

  .rdp-multiple_months .rdp-caption_end .rdp-nav {
    position: absolute;
    top: 0;
    right: 0;
  }

  .rdp-nav {
    display: flex;
    align-items: start;
    gap: ${({ theme }) => theme.spacing.l};
  }

  /* Dropdowns */
  .rdp-dropdown_year,
  .rdp-dropdown_month {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .rdp-dropdown {
    appearance: none;
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    cursor: inherit;
    opacity: 0;
    border: none;
    background-color: transparent;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  .rdp-dropdown[disabled] {
    opacity: unset;
    color: unset;
  }

  .rdp-dropdown:focus-visible:not([disabled]) + .rdp-caption_label {
    border: var(--rdp-outline);
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  .rdp-dropdown_icon {
    margin: 0 0 0 ${({ theme }) => theme.spacing.xxxs};
  }

  .rdp-head {
    border: 0;
  }

  .rdp-head_row,
  .rdp-row {
    height: 100%;
  }

  .rdp-head_cell {
    vertical-align: middle;
    color: var(--rdp-rangestart-color);
    font-size: ${({ theme }) => theme.fontSize.XS};
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    text-align: center;
    height: 100%;
    height: var(--rdp-cell-size);
    padding: 0;
  }

  .rdp-tbody {
    border: 0;
  }

  .rdp-tfoot {
    margin: ${({ theme }) => theme.spacing.xs};
  }

  .rdp-cell {
    width: var(--rdp-cell-size);
    height: 100%;
    padding: 0;
    text-align: center;
  }

  .rdp-weeknumber,
  .rdp-day {
    box-sizing: border-box;
    width: var(--rdp-cell-size);
    height: var(--rdp-cell-size);
    margin: 0;
    border: 1px solid transparent;
    border-radius: 50%;
  }

  .rdp-day_today:not(.rdp-day_outside) {
    background-color: var(--rdp-greyclear-color);
    border: 1.5px solid var(--rdp-greynormal-color);
  }

  .rdp-day_selected,
  .rdp-day_selected:focus {
    background-color: var(--rdp-range-color);
  }

  .rdp-day_selected:focus {
    border: 1.5px solid var(--rdp-greydark-color);
  }

  .rdp-day_selected:focus-visible {
    background-color: var(--rdp-range-color);
  }

  .rdp-day_range_start {
    border: 1px solid var(--rdp-greydark-color);
  }

  .rdp-day_outside {
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    color: var(--rdp-rangestart-color);
  }

  .rdp-day_selected:focus-visible {
    outline: var(--rdp-outline);
    outline-offset: 1px;
    z-index: 1;
  }

  .rdp:not([dir='rtl']) .rdp-day_range_start:not(.rdp-day_range_end) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-left-width: 1px;
    border-left-color: var(--rdp-rangestart-color);
    border-top-width: 1px;
    border-top-color: var(--rdp-rangestart-color);
    border-right-width: 1px;
    border-right-color: var(--rdp-rangestart-color);
    border-bottom-width: 1px;
    border-bottom-color: var(--rdp-rangestart-color);
    background-color: var(--rdp-greyclear-color);
  }

  .rdp:not([dir='rtl']) .rdp-day_range_end:not(.rdp-day_range_start) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-width: 1px;
    border-left-color: var(--rdp-rangestart-color);
    border-top-width: 1px;
    border-top-color: var(--rdp-rangestart-color);
    border-right-width: 1px;
    border-right-color: var(--rdp-rangestart-color);
    border-bottom-width: 1px;
    border-bottom-color: var(--rdp-rangestart-color);
    background-color: var(--rdp-greyclear-color);
  }

  .rdp[dir='rtl'] .rdp-day_range_start:not(.rdp-day_range_end) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .rdp[dir='rtl'] .rdp-day_range_end:not(.rdp-day_range_start) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .rdp-day_range_end.rdp-day_range_start {
    border-radius: 50%;
  }

  .rdp-day_range_middle {
    border-radius: 0;
  }

  /* Responsive Design */
  @media (max-width: ${({ theme }) => theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem){
    .rdp-months {
      gap: ${({ theme }) => theme.spacing.xxl};
    }

    .rdp-weeknumber,
    .rdp-day {
      width: 100%;
      min-width: 35px;
      height: auto;
      aspect-ratio: 1/1;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem){
    .rdp-months {
      flex-direction: column;
    }
  }
`;
